import { useMemo } from 'react';
import { type DateRange } from 'react-day-picker';
import { FormattedMessage } from 'react-intl';

import {
  type DateRangePreset,
  DateRangePicker,
  useDateRangePresetLabels,
} from '@/components/ui/date-range-picker';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';

import { getRangeTimeZoneLabel } from '@/utils/time-zone';

import { DateRangePanel } from '../date-range-panel';
import { TimeZoneChip, TimeZoneChipZone } from '../time-zone-chip';

interface DateRangeDisplayProps {
  appliedDate: DateRange;
  appliedPreset: DateRangePreset;
  onApply: (params: { preset?: DateRangePreset; range: DateRange }) => void;
  /** True while a newly-applied range/preset is still being fetched. */
  isPending?: boolean;
  /** The opening's IANA zone; when set, the panel shows it in a chip below the dates. */
  timeZone?: string;
}

export function DateRangeDisplay({
  appliedDate,
  appliedPreset,
  onApply,
  isPending,
  timeZone,
}: DateRangeDisplayProps) {
  const presetLabels = useDateRangePresetLabels();
  const localDays = useLocalDateRanges();
  // A long range scans one day at a time, so the label is kept until a bound or the
  // zone changes. Lifetime has no applied bounds; today names the zone then.
  const fromTime = appliedDate.from?.getTime();
  const toTime = appliedDate.to?.getTime();
  const timeZoneLabel = useMemo(
    () =>
      timeZone
        ? getRangeTimeZoneLabel(
            fromTime === undefined ? new Date() : new Date(fromTime),
            toTime === undefined ? undefined : new Date(toTime),
            timeZone,
          )
        : null,
    [fromTime, toTime, timeZone],
  );
  const presetSubtitle =
    appliedPreset !== 'custom' ? presetLabels[appliedPreset] : null;

  return (
    <DateRangePanel
      subtitle={presetSubtitle}
      timeZoneChip={
        timeZone &&
        timeZoneLabel && (
          <TimeZoneChip timeZone={timeZone}>
            <FormattedMessage
              defaultMessage="Days run 12:00 AM – 11:59 PM in <zone>{timeZone}</zone>"
              description="Chip below the opening's date range. {timeZone} is an IANA zone with its abbreviation, such as America/Chicago (CDT)"
              values={{
                timeZone: timeZoneLabel,
                zone: chunks => <TimeZoneChipZone>{chunks}</TimeZoneChipZone>,
              }}
            />
          </TimeZoneChip>
        )
      }
    >
      <div className="flex items-center rounded-lg p-2 outline-1 outline-offset-[-1px] outline-neutral-200">
        <DateRangePicker
          selectedDate={appliedDate}
          setSelectedDate={() => {}}
          selectedPreset={appliedPreset}
          onPresetChange={() => {}}
          onApply={onApply}
          showPresetInTrigger={false}
          isLoading={isPending}
          localDays={localDays}
          className="text-fx-neutral-900 [&_svg]:text-fx-neutral-600 h-auto gap-1 p-0! font-medium hover:bg-transparent"
        />
      </div>
    </DateRangePanel>
  );
}
